import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import firebase from 'firebase/compat/app';
import 'firebase/compat/app-check';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import * as firebaseui from 'firebaseui';
import 'firebaseui/dist/firebaseui.css';

import {
  handleFirebaseUiSignInFailure,
  installAnonymousPhoneAccountCollisionRecovery,
} from './utils/firebaseAuthRecovery';
import { getPhoneSigninOptions, observePhoneSigninUi } from './utils/phoneSigninUi';
import { installOnboardingPhoneFunnel } from './utils/onboardingFunnel';


export const FIREBASE_CONFIG = {
    apiKey: "AIzaSyBBuG-peMIN98DKoX4IR1c1uTM1nEIa7U4",
    authDomain: "audio-twinkle.firebaseapp.com",
    projectId: "audio-twinkle",
    storageBucket: "audio-twinkle.appspot.com",
    messagingSenderId: "840715608354",
    appId: "1:840715608354:web:20bce3afdd6e13b1f6057b",
    measurementId: "G-EQJXSGGY6B"
};

// ✅ Initialize Firebase once
if (!firebase.apps.length) {
  firebase.initializeApp(FIREBASE_CONFIG);
}

const appCheckSiteKey =
  process.env.REACT_APP_FIREBASE_APPCHECK_RECAPTCHA_ENTERPRISE_SITE_KEY;
let paidEndpointAppCheck = null;

if (appCheckSiteKey) {
  if (
    process.env.NODE_ENV !== 'production' &&
    process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN
  ) {
    const configuredDebugToken =
      process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
    window.FIREBASE_APPCHECK_DEBUG_TOKEN =
      configuredDebugToken === 'true' ? true : configuredDebugToken;
  }

  paidEndpointAppCheck = firebase.appCheck();
  paidEndpointAppCheck.activate(
    new firebase.appCheck.ReCaptchaEnterpriseProvider(appCheckSiteKey),
    true,
  );
}

export const setFirebaseToken = (token, refreshToken) => {
  const firebaseIdToken = `Bearer ${token}`;
  localStorage.setItem('FirebaseIdToken', firebaseIdToken);
  localStorage.setItem('FirebaseRefreshToken', refreshToken);
  axios.defaults.headers.common['Authorization'] = firebaseIdToken;
};

let tokenRefreshRequest = null;

export const validTokenExists = async () => {
  const savedToken = localStorage.getItem('FirebaseIdToken');
  const refreshToken = localStorage.getItem('FirebaseRefreshToken');
  if (!savedToken?.startsWith('Bearer ') || !refreshToken) {
    const error = new Error('No existing token.');
    error.code = 'auth/no-saved-token';
    throw error;
  }
  let exp;
  try {
    ({ exp } = jwtDecode(savedToken.slice('Bearer '.length)));
    if (!Number.isFinite(exp)) throw new Error('Saved token has no valid expiry.');
  } catch (error) {
    error.code = 'auth/invalid-saved-token';
    throw error;
  }
  if (exp * 1000 > Date.now()) {
    axios.defaults.headers.common.Authorization = savedToken;
    return;
  }
  if (tokenRefreshRequest?.token === refreshToken) return tokenRefreshRequest.promise;
  const current = () => localStorage.getItem('FirebaseIdToken') === savedToken &&
    localStorage.getItem('FirebaseRefreshToken') === refreshToken;
  const request = { token: refreshToken };
  request.promise = axios.post(
    `https://securetoken.googleapis.com/v1/token?key=${FIREBASE_CONFIG.apiKey}`,
    new URLSearchParams({ grant_type: 'refresh_token', refresh_token: refreshToken }).toString(),
    { headers: { 'content-type': 'application/x-www-form-urlencoded', Authorization: undefined } },
  ).then(({ status, data }) => {
    if (!current()) {
      const error = new Error('Account changed.');
      error.code = 'ERR_CANCELED';
      throw error;
    }
    if (status !== 200 || !data?.id_token || !data?.refresh_token) throw new Error('Unable to refresh saved token.');
    setFirebaseToken(data.id_token, data.refresh_token);
  }).finally(() => {
    if (tokenRefreshRequest === request) tokenRefreshRequest = null;
  });
  tokenRefreshRequest = request;
  return request.promise;
};

// ✅ Firebase Auth instance
export const auth = firebase.auth();
export const firestore = firebase.firestore();

let hasResolvedInitialAuthState = false;
let initialAuthStatePromise = null;
let chatV2AnonymousAuthPromise = null;

export const isAnonymousFirebaseUser = (user) => Boolean(user && user.isAnonymous);

export const waitForInitialFirebaseAuthState = () => {
  if (hasResolvedInitialAuthState) {
    return Promise.resolve(auth.currentUser);
  }

  if (!initialAuthStatePromise) {
    initialAuthStatePromise = new Promise((resolve) => {
      const unregisterAuthObserver = auth.onAuthStateChanged((user) => {
        hasResolvedInitialAuthState = true;
        unregisterAuthObserver();
        resolve(user);
      });
    });
  }

  return initialAuthStatePromise;
};

const getCurrentFirebaseUser = async () => {
  if (auth.currentUser) {
    return auth.currentUser;
  }

  return waitForInitialFirebaseAuthState();
};

export const ensureChatV2FirebaseUser = async ({ allowAnonymous = true } = {}) => {
  const currentUser = await getCurrentFirebaseUser();
  if (currentUser && (!isAnonymousFirebaseUser(currentUser) || allowAnonymous)) {
    return currentUser;
  }

  if (!allowAnonymous) {
    return null;
  }

  if (!chatV2AnonymousAuthPromise) {
    chatV2AnonymousAuthPromise = auth
      .signInAnonymously()
      .then((credential) => credential && credential.user ? credential.user : auth.currentUser)
      .finally(() => {
        chatV2AnonymousAuthPromise = null;
      });
  }

  return chatV2AnonymousAuthPromise;
};

export const getChatV2AuthorizationHeader = async ({ allowAnonymous = true } = {}) => {
  const user = await ensureChatV2FirebaseUser({ allowAnonymous });
  if (!user) {
    return null;
  }

  const token = await user.getIdToken();
  return token ? `Bearer ${token}` : null;
};

export const getPaidEndpointAppCheckToken = async () => {
  if (!paidEndpointAppCheck) {
    throw new Error('Unable to verify this app. Please try again.');
  }

  try {
    const result = await paidEndpointAppCheck.getToken(false);
    if (!result || !result.token) {
      throw new Error('Missing App Check token.');
    }
    return result.token;
  } catch {
    throw new Error('Unable to verify this app. Please try again.');
  }
};

// ✅ React component replacing <StyledFirebaseAuth />
export function SigninPopupUI({
  compactPhone = false,
  autoFocusPhone = false,
  onPhoneStageChange,
  onPhoneBusyChange,
  externalPhoneTitles = Boolean(onPhoneStageChange),
} = {}) {
  const widgetContainerRef = useRef(null);
  const onPhoneStageChangeRef = useRef(onPhoneStageChange);
  onPhoneStageChangeRef.current = onPhoneStageChange;
  const onPhoneBusyChangeRef = useRef(onPhoneBusyChange);
  onPhoneBusyChangeRef.current = onPhoneBusyChange;
  const [authUiAttempt, setAuthUiAttempt] = useState(0);
  const [phoneStage, setPhoneStage] = useState(null);
  const hasEnteredCodeRef = useRef(false);
  const [authUiState, setAuthUiState] = useState({
    status: 'starting',
    message: null,
  });

  useEffect(() => {
    let isActive = true;
    let hasAttemptedExistingAccountRecovery = false;
    let patchedAnonymousUser;
    let restoreAnonymousPhoneRecovery = () => {};
    let restorePhoneFunnel = () => {};
    const phoneUiObserver = compactPhone && widgetContainerRef.current
      ? observePhoneSigninUi(widgetContainerRef.current, (stage) => {
          if (isActive) {
            if (stage === 'code') hasEnteredCodeRef.current = true;
            setPhoneStage(stage);
            onPhoneStageChangeRef.current?.(stage);
          }
        })
      : null;
    const claimExistingAccountRecoveryAttempt = () => {
      if (hasAttemptedExistingAccountRecovery) {
        return false;
      }

      hasAttemptedExistingAccountRecovery = true;
      return true;
    };
    const showRecoveryStatus = () => {
      if (isActive) {
        setAuthUiState({
          status: 'recovering',
          message: null,
        });
      }
    };
    const showAuthError = ({ message }) => {
      if (isActive) {
        setAuthUiState({
          status: 'error',
          message,
        });
      }
    };
    const patchAnonymousPhoneConfirmation = (user) => {
      if (user === patchedAnonymousUser) {
        return;
      }

      restorePhoneFunnel();
      restoreAnonymousPhoneRecovery();
      patchedAnonymousUser = user;
      restoreAnonymousPhoneRecovery =
        installAnonymousPhoneAccountCollisionRecovery({
          auth,
          claimRecoveryAttempt:
            claimExistingAccountRecoveryAttempt,
          createPhoneCredential:
            firebase.auth.PhoneAuthProvider.credential,
          onError: showAuthError,
          onRecoveryStart: showRecoveryStatus,
          user,
        });
      restorePhoneFunnel = installOnboardingPhoneFunnel({ auth, user,
        onBusyChange: busy => { if (isActive) onPhoneBusyChangeRef.current?.(busy); },
      });
    };
    const ui =
      firebaseui.auth.AuthUI.getInstance() ||
      new firebaseui.auth.AuthUI(firebase.auth());
    const uiConfig = {
      signInFlow: 'popup',
      autoUpgradeAnonymousUsers: true,
      signInOptions: getPhoneSigninOptions(
        firebase.auth.PhoneAuthProvider.PROVIDER_ID,
        compactPhone,
      ),
      callbacks: {
        signInSuccessWithAuthResult: () => false,
        signInFailure: (error) => handleFirebaseUiSignInFailure({
          auth,
          claimRecoveryAttempt:
            claimExistingAccountRecoveryAttempt,
          error,
          onRecoveryStart: showRecoveryStatus,
          onError: showAuthError,
        }),
        uiShown: () => {
          if (isActive) {
            phoneUiObserver?.refresh();
            setAuthUiState({
              status: 'ready',
              message: null,
            });
          }
        },
      },
    };

    patchAnonymousPhoneConfirmation(auth.currentUser);
    const unregisterAuthObserver = auth.onAuthStateChanged(
      patchAnonymousPhoneConfirmation,
    );
    ui.reset();
    ui.start('#firebaseui-auth-container', uiConfig);

    return () => {
      isActive = false;
      unregisterAuthObserver();
      phoneUiObserver?.disconnect();
      restorePhoneFunnel();
      restoreAnonymousPhoneRecovery();
      ui.reset();
    };
  }, [authUiAttempt, compactPhone]);

  useEffect(() => {
    if (authUiState.status !== 'ready' || phoneStage !== 'phone'
      || (!autoFocusPhone && !hasEnteredCodeRef.current)) return;
    // FirebaseUI first renders inside our hidden loading wrapper. Focus only
    // after it is visible, once per phone step, so blur/dismissal stays respected.
    widgetContainerRef.current?.querySelector('.firebaseui-id-phone-number')?.focus();
  }, [authUiState.status, autoFocusPhone, phoneStage]);

  const tryAgain = () => {
    setAuthUiState({
      status: 'starting',
      message: null,
    });
    setAuthUiAttempt((attempt) => attempt + 1);
  };

  const isWidgetHidden = authUiState.status !== 'ready';

  return (
    <div
      className={`firebaseSigninUi${compactPhone ? ' firebaseSigninUiCompactPhone' : ''}`}
      data-phone-stage-titles={externalPhoneTitles ? 'external' : undefined}
    >
      {(authUiState.status === 'starting' ||
        authUiState.status === 'recovering') && (
        <div className="firebaseSigninStatus" role="status">
          {authUiState.status === 'recovering'
            ? 'Signing you in…'
            : 'Loading sign in…'}
        </div>
      )}
      {authUiState.status === 'error' && (
        <div className="firebaseSigninError">
          <div role="alert">{authUiState.message}</div>
          <button
            type="button"
            className="authSigninPill"
            onClick={tryAgain}
          >
            Try again
          </button>
        </div>
      )}
      <div
        className="firebaseSigninWidget"
        aria-hidden={isWidgetHidden ? 'true' : undefined}
      >
        <div id="firebaseui-auth-container" ref={widgetContainerRef}></div>
      </div>
    </div>
  );
}
